import cuid from "cuid";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACORIS_DEBIT_DAYS,
  BANK_ACCOUNT_OWNER_TYPE,
  CONTACT_POINT_SYSTEM,
  GENDER,
  PAYMENT_FREQUENCIES,
  PAYMENT_TYPES,
} from "utils/constants";
import {
  bindInputProps,
  bindWebTextFieldInputProps,
  setDefaultIfEmpty,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import YesNoBlock from "../Components/YesNoBlock";
import useAcorisZipAddress from "../utils/useAcorisZipAddress";
import {
  ACORIS_ZIP_CODE_LENGTH,
  DEFAULT_ADDRESS_COUNTRY,
  getAcorisCityPlaceholder,
} from "../utils/zipCode";
import validation from "./validation";

function View({
  initialValues,
  subscription,
  project,
  onNext,
  onBack,
  translate,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const countryOptions = useMemo(
    () =>
      (Array.isArray(TRANSLATION.COUNTRIES) ? TRANSLATION.COUNTRIES : []).map(
        (name) => ({ value: name, label: name }),
      ),
    [TRANSLATION.COUNTRIES],
  );

  const [isPayerReceiver, setIsPayerReceiver] = useState(
    get(initialValues, "isPayerReceiver", true),
  );

  const validationSchema = useMemo(
    () =>
      validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        { isPayerReceiver },
      ),
    [isPayerReceiver, t],
  );

  const { submitForm, values, setFieldValue, validateForm, ...formProps } =
    useFormik({
      initialValues: initialValues,
      onSubmit: onNext,
      enableReinitialize: true,
      validateOnMount: true,
      validationSchema: validationSchema,
    });

  const payerZip = get(
    values,
    `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
  );
  const {
    isForeignZip: isForeignPayerZip,
    cities: payerCities,
    citiesLoading,
    changeZip,
  } = useAcorisZipAddress(payerZip);

  useEffect(() => {
    const formikIsPayerReceiver = get(values, "isPayerReceiver", true);
    if (formikIsPayerReceiver !== isPayerReceiver) {
      setIsPayerReceiver(formikIsPayerReceiver);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.isPayerReceiver]);

  useEffect(() => {
    if (validateForm) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPayerReceiver, get(values, "payment.payer.owner.isAdherent")]);

  useEffect(() => {
    if (get(initialValues, "payment.payer.owner.isAdherent")) {
      const payerBase = get(initialValues, "payment.payer", {});
      const owner = {
        id: get(payerBase, "owner.id", cuid()),
        firstName: get(project, "contact.user.firstname"),
        lastName: get(project, "contact.user.lastname"),
        gender: get(project, "contact.user.gender"),
        isAdherent: true,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      };
      setFieldValue("payment.payer.owner", owner);
      if (get(initialValues, "isPayerReceiver", true)) {
        setFieldValue("payment.receiver", {
          ...payerBase,
          owner,
        });
      }
    }
    setIsPayerReceiver(get(initialValues, "isPayerReceiver", true));
    // Only on mount — re-running after refetch would wipe third-party payer input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isThirdPartyPayer = !get(
    values,
    "payment.payer.owner.isAdherent",
    true,
  );

  const syncReceiverWithPayer = (payer, owner) => {
    if (!get(values, "isPayerReceiver", true)) return;
    setFieldValue("payment.receiver", {
      ...payer,
      owner,
    });
  };

  const handleThirdPartyPayerChange = (checked) => {
    if (subscription?.locked) return;
    const payer = get(values, "payment.payer", {});
    if (checked) {
      const owner = {
        ...get(payer, "owner", {}),
        id: get(payer, "owner.id", cuid()),
        isAdherent: false,
        firstName: "",
        lastName: "",
        gender: get(payer, "owner.gender") || GENDER.MALE,
        type: get(payer, "owner.type", BANK_ACCOUNT_OWNER_TYPE.PARTICULAR),
      };
      setFieldValue("payment.payer.owner", owner);
      syncReceiverWithPayer(payer, owner);
      return;
    }
    const owner = {
      ...get(payer, "owner", {}),
      id: get(payer, "owner.id", cuid()),
      isAdherent: true,
      firstName: get(project, "contact.user.firstname", ""),
      lastName: get(project, "contact.user.lastname", ""),
      gender: get(project, "contact.user.gender", GENDER.MALE),
      type: get(payer, "owner.type", BANK_ACCOUNT_OWNER_TYPE.PARTICULAR),
    };
    setFieldValue("payment.payer.owner", owner);
    syncReceiverWithPayer(payer, owner);
    setFieldValue(`telecoms.payer.${CONTACT_POINT_SYSTEM.ADDRESS}.value`, null);
    setFieldValue(
      `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
      null,
    );
    setFieldValue(`telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`, null);
    setFieldValue(
      `telecoms.payer.${CONTACT_POINT_SYSTEM.COUNTRY}.value`,
      DEFAULT_ADDRESS_COUNTRY,
    );
  };

  const handlePayerReceiverSame = () => {
    if (subscription?.locked) return;
    const payer = get(values, "payment.payer");
    setIsPayerReceiver(true);
    setFieldValue("isPayerReceiver", true);
    setFieldValue("payment.receiver", {
      ...payer,
      id: get(payer, "id"),
      IBAN: get(payer, "IBAN", ""),
      BIC: get(payer, "BIC", ""),
      owner: {
        ...get(payer, "owner", {}),
        id: get(payer, "owner.id"),
        firstName: get(payer, "owner.firstName", ""),
        lastName: get(payer, "owner.lastName", ""),
        gender: get(payer, "owner.gender", GENDER.MALE),
        isAdherent: get(payer, "owner.isAdherent", false),
        type: get(payer, "owner.type", BANK_ACCOUNT_OWNER_TYPE.PARTICULAR),
      },
    });
  };

  const handlePayerReceiverDifferent = () => {
    if (subscription?.locked) return;
    setIsPayerReceiver(false);
    setFieldValue("isPayerReceiver", false);
    setFieldValue("payment.receiver", {
      id: cuid(),
      IBAN: "",
      BIC: "",
      owner: {
        id: cuid(),
        firstName: "",
        lastName: "",
        gender: GENDER.MALE,
        isAdherent: false,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      },
    });
  };

  const paymentTypes = setDefaultIfEmpty(
    get(subscription, "insurancePlan.fields.payment.type"),
    [PAYMENT_TYPES.DEBIT],
  );

  return (
    <Layout>
      <Header
        title={translate?.HEADER?.TITLE}
        highlight={translate?.HEADER?.HIGHLIGHT}
        subtitle={translate?.HEADER?.SUBTITLE}
      />
      <div className="acoris-form acoris-bank-information">
        <div className="acoris-form__container">
          <div className="acoris-bank-information__holder-card">
            <div className="acoris-form__row">
              <Input
                placeholder={
                  translate?.FORM?.ACCOUNT_HOLDER_LASTNAME?.PLACEHOLDER ||
                  "Dubois"
                }
                label={
                  translate?.FORM?.ACCOUNT_HOLDER_LASTNAME?.LABEL ||
                  "Nom titulaire du compte"
                }
                disabled={!isThirdPartyPayer}
                {...bindWebTextFieldInputProps({
                  name: "payment.payer.owner.lastName",
                  values,
                  ...formProps,
                })}
              />
              <Input
                placeholder={
                  translate?.FORM?.ACCOUNT_HOLDER_FIRSTNAME?.PLACEHOLDER ||
                  "Michael"
                }
                label={
                  translate?.FORM?.ACCOUNT_HOLDER_FIRSTNAME?.LABEL ||
                  "Prénom titulaire du compte"
                }
                disabled={!isThirdPartyPayer}
                {...bindWebTextFieldInputProps({
                  name: "payment.payer.owner.firstName",
                  values,
                  ...formProps,
                })}
              />
            </div>

            {isThirdPartyPayer && (
              <>
                <div className="acoris-form__row">
                  <Input
                    label={translate?.FORM?.ADDRESS?.LABEL || "Numéro et voie"}
                    placeholder={
                      translate?.FORM?.ADDRESS?.PLACEHOLDER ||
                      "90 rue des roses"
                    }
                    {...bindWebTextFieldInputProps({
                      name: `telecoms.payer.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    label={translate?.FORM?.POSTAL_CODE?.LABEL}
                    placeholder={translate?.FORM?.POSTAL_CODE?.PLACEHOLDER}
                    maxLength={ACORIS_ZIP_CODE_LENGTH}
                    {...bindInputProps({
                      name: `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                      values,
                      ...formProps,
                    })}
                    onChange={({ target: { value } }) => {
                      const { nextZip, reset } = changeZip(value);
                      setFieldValue(
                        `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                        nextZip,
                      );
                      setFieldValue(
                        `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`,
                        reset.city,
                      );
                      if (reset.country !== undefined) {
                        setFieldValue(
                          `telecoms.payer.${CONTACT_POINT_SYSTEM.COUNTRY}.value`,
                          reset.country,
                        );
                      }
                    }}
                  />
                </div>
                <div className="acoris-form__row">
                  {isForeignPayerZip ? (
                    <Input
                      key="payer-city-free"
                      label={translate?.FORM?.CITY?.LABEL}
                      placeholder={getAcorisCityPlaceholder(
                        translate?.FORM?.CITY,
                        true,
                      )}
                      {...bindWebTextFieldInputProps({
                        name: `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`,
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                    />
                  ) : (
                    <Select
                      key="payer-city-select"
                      label={translate?.FORM?.CITY?.LABEL}
                      placeholder={getAcorisCityPlaceholder(
                        translate?.FORM?.CITY,
                        false,
                      )}
                      showSearch
                      loading={citiesLoading}
                      options={payerCities}
                      {...bindInputProps({
                        name: `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`,
                        values,
                        ...formProps,
                      })}
                    />
                  )}
                  {isForeignPayerZip && (
                    <Select
                      showSearch
                      options={countryOptions}
                      label={translate?.FORM?.COUNTRY?.LABEL}
                      placeholder={translate?.FORM?.COUNTRY?.PLACEHOLDER}
                      {...bindInputProps({
                        name: `telecoms.payer.${CONTACT_POINT_SYSTEM.COUNTRY}.value`,
                        values,
                        ...formProps,
                      })}
                    />
                  )}
                </div>
              </>
            )}

            <Input
              type="checkbox"
              variant="round"
              inverted
              checked={isThirdPartyPayer}
              onChange={handleThirdPartyPayerChange}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    translate?.FORM?.THIRD_PARTY_PAYER_HTML ||
                    "Une autre personne règle vos cotisations ? <span>Cochez cette case pour renseigner son identité et ses coordonnées.</span>",
                }}
              />
            </Input>
          </div>

          {paymentTypes.length > 1 && (
            <div className="acoris-form__row">
              <Select
                options={paymentTypes.map((value) => ({
                  value,
                  label:
                    get(translate, `FORM.PAYMENT_TYPE.OPTIONS.${value}`) ||
                    value,
                }))}
                label={get(translate, "FORM.PAYMENT_TYPE.LABEL")}
                placeholder={get(translate, "FORM.SELECT_PLACEHOLDER")}
                {...bindInputProps({
                  name: "payment.type",
                  values,
                  ...formProps,
                })}
              />
            </div>
          )}

          <div className="acoris-form__row">
            <Input
              placeholder={
                translate?.FORM?.BANK_NAME?.PLACEHOLDER || "Nom de la banque"
              }
              label={
                translate?.FORM?.BANK_NAME?.LABEL || "Établissement bancaire"
              }
              {...bindInputProps({
                name: "payment.payer.bankName",
                values,
                ...formProps,
              })}
            />
            <Input
              placeholder={
                translate?.FORM?.BANK_ADDRESS?.PLACEHOLDER ||
                "Adresse de la banque"
              }
              label={
                translate?.FORM?.BANK_ADDRESS?.LABEL ||
                "Adresse de l'établissement"
              }
              {...bindInputProps({
                name: "payment.payer.bankAddress",
                values,
                ...formProps,
              })}
            />
          </div>

          <div className="acoris-form__row">
            <Input
              placeholder={
                translate?.FORM?.IBAN?.PLACEHOLDER ||
                "FR12 3456 7890 1234 5678 9012 345"
              }
              label={translate?.FORM?.IBAN?.LABEL || "IBAN"}
              trim
              {...bindInputProps({
                name: "payment.payer.IBAN",
                values,
                ...formProps,
              })}
              onChange={({ target: { value } }) => {
                setFieldValue("payment.payer.IBAN", value.replace(/\s+/g, ""));
              }}
            />
            <Input
              placeholder={translate?.FORM?.BIC?.PLACEHOLDER || "PRNSFRP1"}
              label={translate?.FORM?.BIC?.LABEL || "BIC"}
              trim
              {...bindInputProps({
                name: "payment.payer.BIC",
                values,
                ...formProps,
              })}
              onChange={({ target: { value } }) => {
                setFieldValue("payment.payer.BIC", value.replace(/\s+/g, ""));
              }}
            />
          </div>

          <div className="acoris-form__row">
            <Select
              options={setDefaultIfEmpty(
                get(subscription, "insurancePlan.fields.payment.frequency"),
                Object.values(PAYMENT_FREQUENCIES),
              ).map((value) => ({
                value,
                label: TRANSLATION.PAYMENT_FREQUENCY.OPTIONS[value],
              }))}
              label={
                translate?.FORM?.PAYMENT_FREQUENCY?.LABEL ||
                TRANSLATION.PAYMENT_FREQUENCY.LABEL
              }
              placeholder={
                translate?.FORM?.SELECT_PLACEHOLDER || "Sélectionnez ..."
              }
              {...bindInputProps({
                name: "payment.frequency",
                values,
                ...formProps,
              })}
            />
            <Select
              options={setDefaultIfEmpty(
                get(subscription, "insurancePlan.fields.payment.debitDay"),
                ACORIS_DEBIT_DAYS,
              ).map((value) => ({
                value,
                label: value,
              }))}
              label={
                translate?.FORM?.DEBIT_DAY?.LABEL || TRANSLATION.DEBIT_DAY.LABEL
              }
              placeholder={
                translate?.FORM?.SELECT_PLACEHOLDER || "Sélectionnez ..."
              }
              {...bindInputProps({
                name: "payment.debitDay",
                values,
                ...formProps,
              })}
            />
          </div>

          <YesNoBlock
            variant={isPayerReceiver ? "pill" : "card"}
            question={
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    translate?.ACTIONS?.QUESTION_HTML ||
                    "Le <strong>compte de prélèvement</strong> des cotisations sera-t-il le <strong>même que le compte de remboursement</strong> des prestations ?",
                }}
              />
            }
            value={isPayerReceiver}
            yesLabel={translate?.ACTIONS?.YES || "Oui"}
            noLabel={translate?.ACTIONS?.NO || "Non"}
            onChange={(next) => {
              if (next) {
                handlePayerReceiverSame();
              } else {
                handlePayerReceiverDifferent();
              }
            }}
          >
            {!isPayerReceiver && (
              <div className="acoris-bank-information__receiver-card">
                <p className="acoris-bank-information__receiver-intro">
                  {translate?.RECEIVER?.INTRO ||
                    "Renseignez vos informations ci-dessous pour le remboursement de vos prestations."}
                </p>
                <div className="acoris-bank-information__receiver-fields">
                  <div className="acoris-form__row">
                    <Input
                      label={
                        translate?.RECEIVER?.LASTNAME?.LABEL ||
                        "Nom titulaire du compte"
                      }
                      placeholder={
                        translate?.RECEIVER?.LASTNAME?.PLACEHOLDER || "Rousseau"
                      }
                      {...bindWebTextFieldInputProps({
                        name: "payment.receiver.owner.lastName",
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      label={
                        translate?.RECEIVER?.FIRSTNAME?.LABEL ||
                        "Prénom titulaire du compte"
                      }
                      placeholder={
                        translate?.RECEIVER?.FIRSTNAME?.PLACEHOLDER || "Bernard"
                      }
                      {...bindWebTextFieldInputProps({
                        name: "payment.receiver.owner.firstName",
                        values,
                        ...formProps,
                      })}
                    />
                  </div>
                  <div className="acoris-form__row">
                    <Input
                      label={translate?.RECEIVER?.IBAN?.LABEL || "IBAN"}
                      placeholder={
                        translate?.RECEIVER?.IBAN?.PLACEHOLDER ||
                        "FR76 3000 4001 2600 0100 6123 456"
                      }
                      trim
                      {...bindInputProps({
                        name: "payment.receiver.IBAN",
                        values,
                        ...formProps,
                      })}
                      onChange={({ target: { value } }) => {
                        setFieldValue(
                          "payment.receiver.IBAN",
                          value.replace(/\s+/g, ""),
                        );
                      }}
                    />
                    <Input
                      label={translate?.RECEIVER?.BIC?.LABEL || "BIC"}
                      placeholder={
                        translate?.RECEIVER?.BIC?.PLACEHOLDER || "PRNSFRP1"
                      }
                      trim
                      {...bindInputProps({
                        name: "payment.receiver.BIC",
                        values,
                        ...formProps,
                      })}
                      onChange={({ target: { value } }) => {
                        setFieldValue(
                          "payment.receiver.BIC",
                          value.replace(/\s+/g, ""),
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </YesNoBlock>

          <ActionButtons
            disabled={!isEmpty(formProps.errors)}
            onNext={submitForm}
            onBack={onBack}
            nextText={translate?.BUTTON?.CONTINUE || "Continuer"}
            backText={translate?.BUTTON?.BACK || "Retour"}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
